// src/pages/krebsgesamt/rs_entit4.js
import React, { useMemo } from "react";
import { Container, Row, Col } from "react-bootstrap";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import rsData from "../../assets/data/RS_SEX_STADIUM.json";
import { SEX_COLORS } from "../../config/colors.js";
import "../../App.css";

const nfPct = new Intl.NumberFormat("de-DE", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});
const fmtPct = (v) =>
  v == null || !Number.isFinite(v) ? "-" : `${nfPct.format(v)}%`;

/** Build 5-year relative survival rows for a given sex ("sex1"=Männer, "sex2"=Frauen) */
function buildRows(data, sexTag) {
  return data
    .filter(
      (d) =>
        d.year === 5 &&
        typeof d.group === "string" &&
        d.group.includes(`_${sexTag}`)
    )
    .map((d) => {
      const entit = d.group.split("_")[0];
      const rel = Number(d.rel_surv);
      return {
        entit,
        rel_surv: Number.isFinite(rel) ? rel : null,
      };
    })
    .filter((r) => r.rel_surv !== null);
}

/** Dark tooltip that always shows positive percents (left chart uses negatives). */
const DarkTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0]?.payload;
  if (!p) return null;
  const v = Math.abs(p.value ?? p.rel_surv);
  return (
    <div
      style={{
        background: "#111",
        color: "#fff",
        padding: "6px 8px",
        borderRadius: 6,
        fontSize: 12,
        lineHeight: 1.35,
        maxWidth: 260,
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{p.entit}</div>
      <div>Relatives 5-Jahres-Überleben: {fmtPct(v)}</div>
    </div>
  );
};

// highlight rule
const HIGHLIGHT_ENTIT = "Krebs gesamt ohne C44";

// custom Y-axis tick renderer (bold only the highlight label)
const makeYAxisTick =
  (textAnchor) =>
  ({ x, y, payload }) => {
    const isBold = payload?.value === HIGHLIGHT_ENTIT;
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={4}
          textAnchor={textAnchor}
          fill="#333"
          style={{ fontSize: 12, fontWeight: isBold ? 700 : 400 }}
        >
          {payload?.value}
        </text>
      </g>
    );
  };

const YTickLeft = makeYAxisTick("end");
const YTickRight = makeYAxisTick("start");

const RelativeSurvival4 = () => {
  // Build & independently sort per-sex rows (DESC)
  const menRows = useMemo(() => {
    const arr = buildRows(rsData, "sex1");
    return arr.sort((a, b) => b.rel_surv - a.rel_surv);
  }, []);
  const womenRows = useMemo(() => {
    const arr = buildRows(rsData, "sex2");
    return arr.sort((a, b) => b.rel_surv - a.rel_surv);
  }, []);

  // Prepare chart data: men negative (bars grow left), women positive (right)
  const menLeftData = useMemo(
    () => menRows.map((r) => ({ ...r, value: -(r.rel_surv ?? 0) })),
    [menRows]
  );
  const womenRightData = useMemo(
    () => womenRows.map((r) => ({ ...r, value: r.rel_surv ?? 0 })),
    [womenRows]
  );

  // Shared settings
  const labelWidth = 260;
  const height = 560;
  const xMax = 110;

  return (
    <Container className="mt-4">
      <h2 className="section-title">Überleben</h2>
      <div className="section-title-underline"></div>
      <div className="aktuelles-text" style={{ marginTop: "0.5rem" }}>
        <p>
          Die Wissenschaft konnte in den letzten Jahrzehnten zu großen Verbesserungen bei der Krebsbehandlung beitragen und viele 
          Erkrankte werden inzwischen erfolgreich therapiert und gelten als geheilt. Die Chancen auf Heilung unterscheiden sich je nach Lokalisation und histologischem 
          Typ der Krebserkrankung. 
          Weitere Faktoren sind das Stadium bei der Diagnose und die Qualität der Behandlung. 
        </p>
        <p>
         Hier sehen sie die relative 5-Jahres-Überlebenswahrscheinlichkeit der häufigsten Krebsarten für den Periodenzeitraum 2019-2023. 
        </p>
          <p>
            Die Überlebenskurven für einzelne Entitäten (getrennt nach Geschlecht bzw. UICC-Stadium) finden Sie{" "}
            <a
              href="https://interaktiverbericht.kr-lsa.de/#/entit/rs_entit">
              hier
            </a>
            .
          </p>
      </div>

      {/* Charts */}
      <Row className="g-0 align-items-end">
        {/* LEFT — Männer */}
        <Col
          md={6}
          xs={12}
          style={{
            paddingRight: 10,
            textAlign: "right", // 👈 align toward center
          }}
        >
          <h5 className="mb-2" style={{ display: "inline-block" }}>
            Männer
          </h5>
          <ResponsiveContainer width="100%" height={height}>
            <BarChart
              data={menLeftData}
              layout="vertical"
              margin={{ top: 10, right: 10, left: 20, bottom: 10 }}
              barCategoryGap={8}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                type="number"
                domain={[-xMax, 0]}
                ticks={[-100, -80, -60, -40, -20, 0]}
                tickFormatter={(v) => `${Math.abs(v)}%`}
              />
              <YAxis
                type="category"
                dataKey="entit"
                width={labelWidth}
                tick={<YTickLeft />}
                axisLine={false}
                tickLine={false}
                orientation="left"
              />
              <Tooltip
                content={<DarkTooltip />}
                cursor={{ fill: "rgba(0,0,0,0.06)" }}
              />
              <Bar
                dataKey="value"
                fill={SEX_COLORS?.M || "#004c3f"}
                isAnimationActive={false}
              />
            </BarChart>
          </ResponsiveContainer>
        </Col>

        {/* RIGHT — Frauen */}
        <Col
          md={6}
          xs={12}
          style={{
            paddingLeft: 10,
            textAlign: "left", // 👈 align toward center
          }}
        >
          <h5 className="mb-2" style={{ display: "inline-block" }}>
            Frauen
          </h5>
          <ResponsiveContainer width="100%" height={height}>
            <BarChart
              data={womenRightData}
              layout="vertical"
              margin={{ top: 10, right: 20, left: 10, bottom: 10 }}
              barCategoryGap={8}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                type="number"
                domain={[0, xMax]}
                ticks={[0, 20, 40, 60, 80, 100]}
                tickFormatter={(v) => `${v}%`}
              />
              <YAxis
                type="category"
                dataKey="entit"
                width={labelWidth}
                tick={<YTickRight />}
                axisLine={false}
                tickLine={false}
                orientation="right"
              />
              <Tooltip
                content={<DarkTooltip />}
                cursor={{ fill: "rgba(0,0,0,0.06)" }}
              />
              <Bar
                dataKey="value"
                fill={SEX_COLORS?.W || "#f0ad00"}
                isAnimationActive={false}
              />
            </BarChart>
          </ResponsiveContainer>
        </Col>
      </Row>
    </Container>
  );
};

export default RelativeSurvival4;



